import { z } from 'zod';
import { transformAST, xml2ast, allHtmlParsers, isASTTextNode } from '@utils/ast';
import { expandSelfClosingTags } from '@utils/common';
import { mediaSchema } from '../../media.js';
import { baseTaskSchema } from '../schema.base.js';
import { fractionInput } from './input.fraction.js';
import { inlineAudioInput } from './input.inline-audio.js';
import { textInput } from './input.text.js';

const writeAnswersInTextTaskSchema = baseTaskSchema.extend({
  taskType: z.literal("WriteAnswers"),
  answerType: z.literal("InText"),
  answerGroups: z.record(z.string(), z.array(z.string())),
  text: z.string(),
  media: z.array(mediaSchema).default([])
}).transform(({
  text,
  answerGroups,
  media,
  ...rest
}) => ({
  ...rest,
  content: transformAST(xml2ast(expandSelfClosingTags(text), {
    ...allHtmlParsers,
    ...inlineAudioInput.parsers,
    ...textInput.parsers,
    ...fractionInput.parsers
  }, "text/html", {
    context: {
      answerGroups,
      taskId: rest.id,
      media
    }
  }),
  // NB: text-inputs should consume text placed directly before and after.
  // We achieve this through this pre-processing step.
  (node, context) => {
    //
    // STEP: Consume prev and next text into text-input nodes
    //
    if (node.nodeType === "text-input") {
      const prevNodeTextToConsume = (() => {
        const prevNode = context.siblingNodes[context.index - 1];
        if (!isASTTextNode(prevNode)) {
          return;
        }
        return prevNode.value.match(/([^\s.,?!]*)$/)?.[1];
      })();
      const nextNodeTextToConsume = (() => {
        const nextNode = context.siblingNodes[context.index + 1];
        if (!isASTTextNode(nextNode)) {
          return;
        }
        return nextNode.value.match(/(^[^\s.,?!]*)/)?.[1];
      })();
      const shouldConsumeContainingWord = prevNodeTextToConsume?.startsWith("__input-i-ord__");
      if (shouldConsumeContainingWord) {
        return {
          ...node,
          containingWord: {
            prefix: prevNodeTextToConsume?.replace(/^__input-i-ord__/, "") ?? null,
            suffix: nextNodeTextToConsume?.replace(/__$/, "") ?? null
          }
        };
      }
    }

    //
    // STEP: Trim text consumed into text-input from text nodes.
    // STEP: add spaces before/after inputs, since it is impossible to see
    // if this has been forgotten in the backend Editor during task
    // creation.
    //
    if (node.nodeType === "text") {
      const prevNode = context.siblingNodes[context.index - 1];
      const nextNode = context.siblingNodes[context.index + 1];
      let newValue = node.value;
      if (prevNode?.nodeType === "text-input") {
        const nodeBeforeTextInput = context.siblingNodes[context.index - 2];
        if (isASTTextNode(nodeBeforeTextInput) && /__input-i-ord__[^\s.,?!]*$/.test(nodeBeforeTextInput.value)) {
          newValue = newValue.replace(/(^[^\s.,?!]*)/, "");
        }
        if (/[^\s.,?!]/.test(newValue[0] ?? "")) {
          newValue = ` ${newValue}`;
        }
      }
      if (nextNode?.nodeType === "text-input") {
        if (/__input-i-ord__[^\s.,?!]*$/.test(node.value)) {
          newValue = newValue.replace(/([^\s.,?!]*)$/, "");
        }
        if (/[^\s.,?!]/.test(newValue.at(-1) ?? "")) {
          newValue = `${newValue} `;
        }
      }
      return {
        ...node,
        value: newValue
      };
    }
    return node;
  })
}));

export { writeAnswersInTextTaskSchema };
//# sourceMappingURL=_schema.js.map
